// Dashboard API — the micro1/zara HR-dashboard UI, wired to Duty AI's real
// backend endpoints (roadmap #19). Replaces micro1's mock.js + its own backend.
import axios from 'axios';
import { attachBearer } from '../../lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,           // send the session cookie (legacy path)
  headers: { 'Content-Type': 'application/json' },
});

// Carry the session token (Bearer) on every dashboard call — set at Google login.
// Cross-subdomain safe (dashboard on dutyai.in, API on api.dutyai.in).
attachBearer(client);

// Analytics — GET /api/analytics/summary (real aggregation from interview_sessions)
export const analyticsApi = {
  summary: () => client.get('/analytics/summary').then((r) => r.data),
};

// AI builder — /api/ai-builder/* (exact match to micro1's /ai/*)
export const aiApi = {
  generateSkillQuestions: (skill, description, count) =>
    client.post('/ai-builder/generate-skill-questions', { skill, description, count }).then((r) => r.data),
  generateExercise: (exercise_type, topic, context) =>
    client.post('/ai-builder/generate-exercise', { exercise_type, topic, context }).then((r) => r.data),
  generateMcqs: (context, count) =>
    client.post('/ai-builder/generate-mcqs', { context, count }).then((r) => r.data),
  draftInterview: (job_description) =>
    client.post('/ai-builder/draft-interview', { job_description }, { timeout: 90000 }).then((r) => r.data),
};

// Interview templates — /api/interview-templates
export const templatesApi = {
  list: () => client.get('/interview-templates').then((r) => r.data),
  create: (payload) => client.post('/interview-templates', payload).then((r) => r.data),
  remove: (id) => client.delete(`/interview-templates/${id}`).then((r) => r.data),
  publish: (id) => client.post(`/interview-templates/${id}/publish`).then((r) => r.data),
};

// HR settings — /api/hr-settings
export const settingsApi = {
  get: () => client.get('/hr-settings').then((r) => r.data),
  update: (payload) => client.put('/hr-settings', payload).then((r) => r.data),
};

// "Interviews" in the builder UI == authored interview definitions, which are
// Duty AI's interview-templates (same {name, opening_phrase, total_duration,
// flow} model). Map the full CRUD there.
export const interviewsApi = {
  list: () => client.get('/interview-templates').then((r) => r.data),
  get: (id) => client.get(`/interview-templates/${id}`).then((r) => r.data),
  create: (payload) => client.post('/interview-templates', payload).then((r) => r.data),
  update: (id, patch) => client.put(`/interview-templates/${id}`, patch).then((r) => r.data),
  remove: (id) => client.delete(`/interview-templates/${id}`).then((r) => r.data),
};

// Frappe admin — real endpoints (/api/frappe/*, recruiter-gated). Jobs reuse
// the public list; shape matches what OpenJobs expects.
export const frappeApi = {
  status: () => client.get('/frappe/status').then((r) => r.data),
  saveConfig: (cfg) => client.put('/frappe/config', cfg).then((r) => r.data),
  sync: () => client.post('/frappe/sync').then((r) => r.data),
  jobs: () =>
    client.get('/public/jobs').then((r) => ({ jobs: r.data?.jobs ?? [], live: true })),
};
